// Lib
import { CSSProperties, RefObject, useMemo } from 'react';

// Utils
import { getCroppedImageHeight, getCroppedImageWidth } from '../../imageModalUtils';
import { fitSizeWithinBounds } from '../../imageModalSizingUtils';
import { useMeasuredElementRect } from '../../../../../utils/dom/useMeasuredElementRect';

// Types
import { ImMNElement } from '../../../../../../common/elements/elementModelTypes';
import { RectSize } from '../../../../../../common/maths/geometry/rect/rectTypes';

const TOOLBAR_BOTTOM_GAP = 16;
const IMAGE_AREA_HORIZONTAL_PADDING = 60;
const IMAGE_AREA_TOP_PADDING = 40;
const IMAGE_AREA_BOTTOM_PADDING = 40;

type ImageModalV2LayoutProps = {
    element: ImMNElement;
    attachment?: Record<string, unknown>;
    gridSize: number;
    modalRef: RefObject<HTMLElement>;
    captionRef: RefObject<HTMLElement>;
};

type ImageModalV2Layout = {
    imageAreaStyle: CSSProperties;
    editOverlayStyle: CSSProperties;
    contentStyle: CSSProperties;
    imageWrapperStyle: CSSProperties;
    toolbarStyle: CSSProperties;
    zoomViewportSize: RectSize;
    imageBounds: RectSize;
};

type ImageModalV2LayoutCalculation = {
    modalSize: RectSize;
    imageSize: RectSize;
    captionSize: RectSize;
};

/**
 * Calculates V2 image modal geometry from measured DOM sizes.
 *
 * This is the only place that decides how much room the image gets. CSS provides the shell,
 * while these measured values reserve stable modal padding and caption height before fitting
 * the image into the available viewport area.
 */
export const calculateImageModalV2Layout = ({ modalSize, imageSize, captionSize }: ImageModalV2LayoutCalculation) => {
    const captionHeight = captionSize.height || 0;
    const imageAreaHeight = modalSize.height;
    const zoomViewportSize = {
        width: Math.max(modalSize.width - IMAGE_AREA_HORIZONTAL_PADDING * 2, 0),
        height: Math.max(imageAreaHeight - IMAGE_AREA_TOP_PADDING - IMAGE_AREA_BOTTOM_PADDING, 0),
    };
    const imageBounds = {
        width: zoomViewportSize.width,
        height: Math.max(imageAreaHeight - IMAGE_AREA_TOP_PADDING - IMAGE_AREA_BOTTOM_PADDING - captionHeight, 0),
    };
    const fittedImageSize = fitSizeWithinBounds(imageSize, imageBounds);

    return {
        imageAreaStyle: {
            flexBasis: imageAreaHeight,
            height: imageAreaHeight,
            paddingTop: IMAGE_AREA_TOP_PADDING,
            paddingRight: IMAGE_AREA_HORIZONTAL_PADDING,
            paddingBottom: IMAGE_AREA_BOTTOM_PADDING,
            paddingLeft: IMAGE_AREA_HORIZONTAL_PADDING,
        },
        editOverlayStyle: {
            top: IMAGE_AREA_TOP_PADDING,
            right: IMAGE_AREA_HORIZONTAL_PADDING,
            bottom: IMAGE_AREA_BOTTOM_PADDING,
            left: IMAGE_AREA_HORIZONTAL_PADDING,
        },
        contentStyle: {
            width: fittedImageSize.width,
            height: fittedImageSize.height + captionHeight,
        },
        imageWrapperStyle: fittedImageSize,
        toolbarStyle: {
            bottom: TOOLBAR_BOTTOM_GAP,
        },
        zoomViewportSize,
        // The full area available to the image (caption height already reserved). The editor fits the
        // uncropped/rotated source image into this, rather than the cropped image's smaller footprint.
        imageBounds,
    };
};

/**
 * Builds the measured layout used by the V2 image modal shell.
 */
export const useImageModalV2Layout = ({
    element,
    attachment,
    gridSize,
    modalRef,
    captionRef,
}: ImageModalV2LayoutProps): ImageModalV2Layout => {
    // These are the layout inputs that can change after first render:
    // viewport/modal size and TipTap caption height.
    const modalSize = useMeasuredElementRect(modalRef);
    const captionSize = useMeasuredElementRect(captionRef);

    // The source image dimensions come from image metadata and crop state, not from the rendered DOM.
    const imageSize = useMemo(
        () => ({
            width: getCroppedImageWidth({ element, gridSize, attachment }),
            height: getCroppedImageHeight({ element, gridSize, attachment }),
        }),
        [attachment, element, gridSize],
    );

    // Once the measurements are known, layout becomes a pure calculation that returns inline styles
    // for the image area, content wrapper, image itself and toolbar position.
    return useMemo(
        () => calculateImageModalV2Layout({ modalSize, imageSize, captionSize }),
        [captionSize, imageSize, modalSize],
    );
};
